import type {
  ChainModifiers,
  Entry,
  EntryFieldTypes,
  EntrySkeletonType,
  LocaleCode,
} from 'contentful';
import type { TypeActionBannerFullSkeleton } from './TypeActionBannerFull';
import type { TypeActionCardsSkeleton } from './TypeActionCards';
import type { TypeActionDropdownSkeleton } from './TypeActionDropdown';
import type { TypeActionUserInputSkeleton } from './TypeActionUserInput';
import type { TypeActionUserInputHorizontalSkeleton } from './TypeActionUserInputHorizontal';
import type { TypeCalloutContentSkeleton } from './TypeCalloutContent';
import type { TypeComparisonTableContainerSkeleton } from './TypeComparisonTableContainer';
import type { TypeEcommIntakeSkeleton } from './TypeEcommIntake';
import type { TypeEditorialHeadingParagraphSkeleton } from './TypeEditorialHeadingParagraph';
import type { TypeExitIntentSkeleton } from './TypeExitIntent';
import type { TypeFaqinfoAccordionSkeleton } from './TypeFaqinfoAccordion';
import type { TypeFeatureCardSkeleton } from './TypeFeatureCard';
import type { TypeGlobalFaqSkeleton } from './TypeGlobalFaq';
import type { TypeGlobalListSkeleton } from './TypeGlobalList';
import type { TypeHeaderMenuItemSkeleton } from './TypeHeaderMenuItem';
import type { TypeHeroCarouselSkeleton } from './TypeHeroCarousel';
import type { TypeHeroFullOverlaySkeleton } from './TypeHeroFullOverlay';
import type { TypeHeroImageTextFiftyFiftySkeleton } from './TypeHeroImageTextFiftyFifty';
import type { TypeHeroLeadFormSkeleton } from './TypeHeroLeadForm';
import type { TypeHeroMasterFormSkeleton } from './TypeHeroMasterForm';
import type { TypeHeroMidFullImageSkeleton } from './TypeHeroMidFullImage';
import type { TypeHeroOneHalfImageSkeleton } from './TypeHeroOneHalfImage';
import type { TypeHeroSmallTwoThirdsImageSkeleton } from './TypeHeroSmallTwoThirdsImage';
import type { TypeHeroTestimonialsSkeleton } from './TypeHeroTestimonials';
import type { TypeHeroTwoThirdsImageSkeleton } from './TypeHeroTwoThirdsImage';
import type { TypeHighlightCardsComponentSkeleton } from './TypeHighlightCardsComponent';
import type { TypeHighlightContactCardsSkeleton } from './TypeHighlightContactCards';
import type { TypeHighlightIconTextStackedSkeleton } from './TypeHighlightIconTextStacked';
import type { TypeHighlightImageTextSkeleton } from './TypeHighlightImageText';
import type { TypeHighlightImageTextStackedSkeleton } from './TypeHighlightImageTextStacked';
import type { TypeHighlightLightboxSkeleton } from './TypeHighlightLightbox';
import type { TypeHighlightTestimonialSkeleton } from './TypeHighlightTestimonial';
import type { TypeHighlightTextSkeleton } from './TypeHighlightText';
import type { TypeHighlightVideoInlineSkeleton } from './TypeHighlightVideoInline';
import type { TypeImageGrid3ColWithDescriptionSkeleton } from './TypeImageGrid3ColWithDescription';
import type { TypeImageGridFourCirclesTextSkeleton } from './TypeImageGridFourCirclesText';
import type { TypeImageSectionSkeleton } from './TypeImageSection';
import type { TypeInteractiveMapSkeleton } from './TypeInteractiveMap';
import type { TypeInteractiveQuoteFormSkeleton } from './TypeInteractiveQuoteForm';
import type { TypeLeadFormContainerSkeleton } from './TypeLeadFormContainer';
import type { TypeLinkSkeleton } from './TypeLink';
import type { TypeOneHalfImageSkeleton } from './TypeOneHalfImage';
import type { TypeOneThirdImageWithCopySkeleton } from './TypeOneThirdImageWithCopy';
import type { TypePaidLanderSkeleton } from './TypePaidLander';
import type { TypeRichTextSkeleton } from './TypeRichText';
import type { TypeSectionStateConstraintSkeleton } from './TypeSectionStateConstraint';
import type { TypeSunrunShopProductGridSkeleton } from './TypeSunrunShopProductGrid';
import type { TypeSwitchableContentSelectorSkeleton } from './TypeSwitchableContentSelector';
import type { TypeTaxFormSectionSkeleton } from './TypeTaxFormSection';
import type { TypeUniversalEmbedSkeleton } from './TypeUniversalEmbed';
import type { TypeWayfindingSkeleton } from './TypeWayfinding';

export interface TypeSectionConstraintsFields {
  constraintName: EntryFieldTypes.Symbol;
  default?: EntryFieldTypes.Array<
    EntryFieldTypes.EntryLink<
      | TypeActionBannerFullSkeleton
      | TypeActionCardsSkeleton
      | TypeActionDropdownSkeleton
      | TypeActionUserInputHorizontalSkeleton
      | TypeActionUserInputSkeleton
      | TypeCalloutContentSkeleton
      | TypeComparisonTableContainerSkeleton
      | TypeEcommIntakeSkeleton
      | TypeEditorialHeadingParagraphSkeleton
      | TypeExitIntentSkeleton
      | TypeFaqinfoAccordionSkeleton
      | TypeFeatureCardSkeleton
      | TypeGlobalFaqSkeleton
      | TypeGlobalListSkeleton
      | TypeHeaderMenuItemSkeleton
      | TypeHeroCarouselSkeleton
      | TypeHeroFullOverlaySkeleton
      | TypeHeroImageTextFiftyFiftySkeleton
      | TypeHeroLeadFormSkeleton
      | TypeHeroMasterFormSkeleton
      | TypeHeroMidFullImageSkeleton
      | TypeHeroOneHalfImageSkeleton
      | TypeHeroSmallTwoThirdsImageSkeleton
      | TypeHeroTestimonialsSkeleton
      | TypeHeroTwoThirdsImageSkeleton
      | TypeHighlightCardsComponentSkeleton
      | TypeHighlightContactCardsSkeleton
      | TypeHighlightIconTextStackedSkeleton
      | TypeHighlightImageTextSkeleton
      | TypeHighlightImageTextStackedSkeleton
      | TypeHighlightLightboxSkeleton
      | TypeHighlightTestimonialSkeleton
      | TypeHighlightTextSkeleton
      | TypeHighlightVideoInlineSkeleton
      | TypeImageGrid3ColWithDescriptionSkeleton
      | TypeImageGridFourCirclesTextSkeleton
      | TypeImageSectionSkeleton
      | TypeInteractiveMapSkeleton
      | TypeInteractiveQuoteFormSkeleton
      | TypeLeadFormContainerSkeleton
      | TypeLinkSkeleton
      | TypeOneHalfImageSkeleton
      | TypeOneThirdImageWithCopySkeleton
      | TypePaidLanderSkeleton
      | TypeRichTextSkeleton
      | TypeSunrunShopProductGridSkeleton
      | TypeSwitchableContentSelectorSkeleton
      | TypeTaxFormSectionSkeleton
      | TypeUniversalEmbedSkeleton
      | TypeWayfindingSkeleton
    >
  >;
  constraints: EntryFieldTypes.Array<
    EntryFieldTypes.EntryLink<TypeSectionStateConstraintSkeleton>
  >;
}

export type TypeSectionConstraintsSkeleton = EntrySkeletonType<
  TypeSectionConstraintsFields,
  'sectionConstraints'
>;
export type TypeSectionConstraints<
  Modifiers extends ChainModifiers,
  Locales extends LocaleCode = LocaleCode,
> = Entry<TypeSectionConstraintsSkeleton, Modifiers, Locales>;

export function isTypeSectionConstraints<
  Modifiers extends ChainModifiers,
  Locales extends LocaleCode,
>(
  entry: Entry<EntrySkeletonType, Modifiers, Locales>
): entry is TypeSectionConstraints<Modifiers, Locales> {
  return entry.sys.contentType.sys.id === 'sectionConstraints';
}

export type TypeSectionConstraintsWithoutLinkResolutionResponse =
  TypeSectionConstraints<'WITHOUT_LINK_RESOLUTION'>;
export type TypeSectionConstraintsWithoutUnresolvableLinksResponse =
  TypeSectionConstraints<'WITHOUT_UNRESOLVABLE_LINKS'>;
export type TypeSectionConstraintsWithAllLocalesResponse<
  Locales extends LocaleCode = LocaleCode,
> = TypeSectionConstraints<'WITH_ALL_LOCALES', Locales>;
export type TypeSectionConstraintsWithAllLocalesAndWithoutLinkResolutionResponse<
  Locales extends LocaleCode = LocaleCode,
> = TypeSectionConstraints<
  'WITHOUT_LINK_RESOLUTION' | 'WITH_ALL_LOCALES',
  Locales
>;
export type TypeSectionConstraintsWithAllLocalesAndWithoutUnresolvableLinksResponse<
  Locales extends LocaleCode = LocaleCode,
> = TypeSectionConstraints<
  'WITHOUT_UNRESOLVABLE_LINKS' | 'WITH_ALL_LOCALES',
  Locales
>;
